import {
  CreateVisitFetcherArg,
  VisitFetcher,
  ExpirationDate,
  VisitToken,
  VisitError,
} from "./types";
const VISIT_ENDPOINT = "/travel/service/frontendapi/getVisitInformation";
const APPLICATION_JSON = "application/json;charset=UTF-8";
const REFRESH_AFTER_MINUTES = 25;
const MILLISECONDS_PER_MINUTE = 60 * 1000;
const minutesFromNow = (minutes: number): number =>
  Date.now() + minutes * MILLISECONDS_PER_MINUTE;
const headers = {
  "Content-Type": APPLICATION_JSON,
};
export const createVisitFetcher = ({
  tokenFallback,
  logger,
}: CreateVisitFetcherArg): VisitFetcher => {
  let currentCall: Promise<VisitToken> | null = null;
  let expirationTime: ExpirationDate = null;
  let token: VisitToken = tokenFallback;
  const isExpired = (): boolean => {
    return !token || (!!expirationTime && Date.now() >= expirationTime);
  };
  const checkFetch = (error: Error): Promise<Response> => {
    return Promise.reject({
      error,
      message: "OF2_VISIT - error fetching",
    });
  };
  const checkResponse = (response: Response): Promise<Response> => {
    if (!response.ok) {
      return Promise.reject({
        error: new Error(response.statusText),
        message: `OF2_VISIT - status code ${response.status}`,
      });
    }
    return Promise.resolve(response);
  };
  const getToken = (response: Response): Promise<VisitToken> => {
    return response.text().catch((error) => {
      return Promise.reject({
        error,
        message: `OF2_VISIT - error response.text(): Content-Type is ${response.headers.get(
          "content-type"
        )}`,
      });
    });
  };
  const parseToken = (
    tokenResponse: VisitToken
  ): VisitToken | Promise<VisitToken> => {
    if (tokenResponse) {
      expirationTime = minutesFromNow(REFRESH_AFTER_MINUTES);
    } else {
      return Promise.reject({
        error: new Error("empty token"),
        message: "OF2_VISIT - empty token",
      });
    }
    if (token !== tokenResponse) {
      logger.error(
        new Error("different visit"),
        `OF2_VISIT - different visit: ${tokenResponse} vs ${token}`
      );
      token = tokenResponse;
    }
    return token;
  };
  const handleError = ({ error, message }: VisitError): VisitToken => {
    logger.error(error, message);
    return token;
  };
  const fetchVisit = (): Promise<VisitToken> =>
    fetch(VISIT_ENDPOINT, {
      credentials: "same-origin",
      method: "GET",
      headers,
    })
      .then(checkResponse, checkFetch)
      .then(getToken)
      .then(parseToken)
      .catch(handleError)
      .finally(() => {
        currentCall = null;
      });
  return () => {
    if (!currentCall) {
      currentCall = fetchVisit();
      if (!tokenFallback) {
        return currentCall;
      }
    }
    if (isExpired()) {
      return currentCall;
    } else {
      return Promise.resolve(token);
    }
  };
};
